"use client";

import React from "react";
import { Calendar, Tag, ArrowLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";

function MainPostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto animate-pulse">
        <div className="h-8 w-32 bg-muted/30 rounded mb-8"></div>
        <div className="h-16 w-3/4 bg-muted/30 rounded mb-6 mx-auto"></div>
        <div className="h-6 w-1/2 bg-muted/30 rounded mb-12 mx-auto"></div>
        <div className="h-96 w-full bg-muted/30 rounded-2xl mb-12"></div>
        <div className="space-y-4">
          <div className="h-4 bg-muted/30 rounded w-full"></div>
          <div className="h-4 bg-muted/30 rounded w-full"></div>
          <div className="h-4 bg-muted/30 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <p className="text-destructive font-default text-lg">Errore nel caricamento del dossier.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <p className="text-muted-foreground font-default text-lg">Dossier non trovato.</p>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto">
      <div className="mb-8">
        <Link to="/HomePage">
          <Button variant="ghost" className="text-muted-foreground hover:text-secondary pl-0 hover:bg-transparent">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Torna all'Osservatorio
          </Button>
        </Link>
      </div>

      <header className="mb-12 text-center">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-6 flex justify-center gap-2 flex-wrap">
            {post.categories.map((cat, i) => (
              <span
                key={cat.id}
                data-index={i}
                className="text-xs font-bold uppercase tracking-widest text-secondary bg-secondary/10 px-3 py-1 rounded-none font-default border border-secondary/20"
              >
                {cat.name}
              </span>
            ))}
          </div>
        )}
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold mb-8 leading-tight text-foreground">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground font-default">
          {post.author && (
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 border border-border">
                <AvatarImage src={post.author.avatar?.['48']} alt={post.author.name} />
                <AvatarFallback className="bg-muted text-foreground font-default font-semibold">
                  {post.author.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}
          
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-secondary" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString('it-IT', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-16 rounded-none overflow-hidden border border-border shadow-2xl">
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="w-full h-auto object-cover aspect-video grayscale hover:grayscale-0 transition-all duration-700" 
          />
        </div>
      )}

      <div
        className="prose prose-invert prose-lg max-w-none font-sans text-muted-foreground leading-relaxed
                   [&>p]:mb-8 [&>p]:text-lg md:[&>p]:text-xl
                   [&>h2]:text-3xl [&>h2]:font-default [&>h2]:font-bold [&>h2]:text-foreground [&>h2]:mt-16 [&>h2]:mb-6 [&>h2]:tracking-tight
                   [&>h3]:text-2xl [&>h3]:font-default [&>h3]:font-bold [&>h3]:text-foreground [&>h3]:mt-10 [&>h3]:mb-4
                   [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-8 [&>ul]:text-lg
                   [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-8 [&>ol]:text-lg
                   [&>blockquote]:border-l-4 [&>blockquote]:border-secondary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-foreground [&>blockquote]:my-10 [&>blockquote]:text-2xl [&>blockquote]:bg-muted/20 [&>blockquote]:py-4
                   [&>img]:rounded-none [&>img]:my-10 [&>img]:border [&>img]:border-border
                   [&>a]:text-secondary [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-secondary/80"
        dangerouslySetInnerHTML={{ __html: post.content || post.excerpt || "" }}
      />

      {post.tags && post.tags.length > 0 && (
        <div className="mt-16 pt-8 border-t border-border flex items-center gap-3 flex-wrap">
          <Tag className="h-5 w-5 text-secondary" />
          <span className="text-sm font-default font-medium text-foreground mr-2">Classificazione:</span>
          {post.tags.map((tag, i) => (
            <Link 
              key={tag.id} 
              to={tag.link || "#"}
              data-index={i}
            >
              <Button variant="secondary" size="sm" className="rounded-none text-xs h-7 bg-muted hover:bg-secondary hover:text-secondary-foreground transition-colors">
                {tag.name}
              </Button>
            </Link>
          ))}
        </div>
      )}
    </article>
  );
}

function RelatedPostsGrid() {
  const { posts, loading, error } = usePosts();

  if (loading || error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="mt-24 max-w-6xl mx-auto border-t border-border pt-16">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-default font-bold text-foreground mb-4">
          Intelligence Correlata
        </h2>
        <p className="text-muted-foreground font-default">
          Approfondisci le dinamiche del mercato digitale russo e CSI.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {posts.slice(0, 3).map((post, i) => (
          <Card 
            key={post.id} 
            data-index={i}
            className="bg-card border border-border rounded-none overflow-hidden hover:border-secondary/50 transition-all group flex flex-col shadow-xl"
          >
            {post.featuredImage && (
              <div className="aspect-video overflow-hidden border-b border-border">
                <img 
                  src={post.featuredImage} 
                  alt={post.title} 
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-transform duration-700" 
                />
              </div>
            )}
            <CardHeader className="flex-grow pt-6">
              {post.categories && post.categories.length > 0 && (
                <span className="text-xs font-bold text-secondary uppercase tracking-widest mb-3 block font-default">
                  {post.categories[0].name}
                </span>
              )}
              <CardTitle className="font-default text-xl leading-snug group-hover:text-secondary transition-colors">
                <Link to={post.link || "#"}>
                  {post.title}
                </Link>
              </CardTitle>
            </CardHeader>
            <CardFooter className="pb-6">
              <Link to={post.link || "#"}>
                <Button variant="link" className="px-0 text-muted-foreground hover:text-secondary font-default">
                  Leggi Dossier <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const current_wp_query_params = typeof window !== 'undefined' && (window as any).wvcClient 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: 'post' };
    
  const main_wp_query = new WP_Query(current_wp_query_params);

  const related_wp_query = new WP_Query({ 
    post_type: 'post', 
    per_page: 3,
    orderby: 'date',
    order: 'desc'
  });

  return (
    <section data-section-id="233965" 
      id="single-post-template-section" 
      className="bg-background text-foreground py-16 md:py-24 px-4 md:px-8 min-h-screen"
    >
      <PostsProvider wp_query={main_wp_query}>
        <MainPostContent />
      </PostsProvider>

      <PostsProvider wp_query={{ post_type: "post", per_page: 3, orderby: "date", order: "desc" }}>
        <RelatedPostsGrid />
      </PostsProvider>
    </section>
  );
}